import { DeliveryServiceabilityStatus } from '@/lib/enums/DeliveryServiceabilityStatus'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { hasPositiveFsnOrderProductValue } from '@/lib/services/fsn-network/fsn-network.utils'
import type { SalesOrder } from '@/lib/services/sales-orders/sales-orders.types'
import { normalizePhoneDigits } from '@/lib/utils'

/** The order fields a send to Flower Shop Network reads, as the order page and the POS hold them. */
export type FsnNetworkEligibilityOrder = Partial<
  Pick<
    SalesOrder,
    | 'fulfillment_type'
    | 'delivery_serviceability_status'
    | 'cancelled_at'
    | 'fsn_network_payment_blocker'
    | 'fulfillment_due_date'
    | 'recipient_name'
    | 'recipient_phone'
    | 'card_message'
    | 'signature'
  >
> & {
  order_source_link?: Pick<NonNullable<SalesOrder['order_source_link']>, 'source_system'> | null
  delivery_address?: Partial<NonNullable<SalesOrder['delivery_address']>> | null
  sales_order_items?:
    | readonly Partial<NonNullable<SalesOrder['sales_order_items']>[number]>[]
    | null
  fsn_network_order_preview?: Partial<NonNullable<SalesOrder['fsn_network_order_preview']>> | null
}

export interface FsnNetworkOrderEligibility {
  isEligible: boolean
  errors: string[]
}

export function hasFsnNetworkCardDetails(
  order: Pick<FsnNetworkEligibilityOrder, 'card_message' | 'signature'> = {}
): boolean {
  return Boolean(String(order.card_message ?? '').trim() || String(order.signature ?? '').trim())
}

export function getFsnNetworkOrderEligibility(
  order: FsnNetworkEligibilityOrder = {},
  { allowMissingCardDetails = false }: { allowMissingCardDetails?: boolean } = {}
): FsnNetworkOrderEligibility {
  const errors: string[] = []

  if (order.order_source_link?.source_system === 'condolences') {
    errors.push('Condolences orders cannot be sent through Flower Shop Network.')
  }

  if (order.fulfillment_type !== FulfillmentType.Delivery.Value) {
    errors.push('Only delivery orders can be sent through Flower Shop Network.')
  }

  if (
    order.delivery_serviceability_status != null &&
    order.delivery_serviceability_status !== DeliveryServiceabilityStatus.OutsideDeliveryArea.Value
  ) {
    errors.push('Save an outside-area delivery address before sending to Flower Shop Network.')
  }

  if (order.cancelled_at) {
    errors.push('Cancelled or abandoned orders cannot be sent through Flower Shop Network.')
  }

  // The order's money is the API's to judge: paid, or a customer who may carry it on Pay Later within their credit limit.
  if (order.fsn_network_payment_blocker) {
    errors.push(order.fsn_network_payment_blocker)
  }

  if (!order.fulfillment_due_date) {
    errors.push('Add a delivery date.')
  }

  if (!String(order.recipient_name ?? '').trim()) {
    errors.push('Add a recipient name.')
  }

  if (normalizePhoneDigits(order.recipient_phone).length !== 10) {
    errors.push('Add a 10-digit recipient phone number.')
  }

  if (!allowMissingCardDetails && !hasFsnNetworkCardDetails(order)) {
    errors.push('Add a card message or signature.')
  }

  const address = order.delivery_address
  if (!String(address?.line1 ?? '').trim()) errors.push('Add a delivery address.')
  if (!String(address?.city ?? '').trim()) errors.push('Add a delivery city.')
  if (!String(address?.state ?? '').trim()) errors.push('Add a delivery state.')
  if (!String(address?.postal_code ?? '').trim()) errors.push('Add a delivery zip code.')

  if (!Array.isArray(order.sales_order_items) || order.sales_order_items.length === 0) {
    errors.push('Add at least one item.')
  } else if (!hasPositiveFsnOrderProductValue(order)) {
    errors.push('Add at least one positive-price item.')
  }

  return {
    isEligible: errors.length === 0,
    errors,
  }
}
